/**
 * Chat Tourvisio Service — Guided Search Flow
 * 
 * Implements a step-by-step tour consultant flow:
 * Step 1: Departure (getDepartures)
 * Step 2: Destination (getArrivals + destination enrichment)
 * Step 3: Check-in date (getCheckInDates)
 * Step 4: Nights (getNights)
 * Step 5: Travelers (adults + children)
 * Step 6: Execute search (searchTourvisio)
 */

import { searchTourvisio, getArrivals, getDepartures, getCheckInDates, getNights, getArrivalAutoComplete } from './tourvisioService';
import type { SearchQuery, HolidayPackage, Hotel, Tour, ApiLogEntry, Language, LocationOption, DateOption, NightOption } from '../types';
import { SearchType } from '../types';

// ==================== TYPES ====================

export type ChatStep = 'idle' | 'ask_search_type' | 'ask_departure' | 'ask_destination' | 'ask_dates' | 'ask_nights' | 'ask_travelers' | 'searching' | 'done';

export interface ChatSearchState {
    step: ChatStep;
    searchType: 'package' | 'hotel_only';
    departureId: string;
    departureName: string;
    departureType: number;
    destinationId: string;
    destinationName: string;
    destinationType: number;
    checkIn: string;
    nights: number;
    adults: number;
    children: number;
    childrenAges: number[];
}

export interface ChatChip {
    label: string;
    value: string;
    icon?: string;
    type?: number;      // location type for API calls
    parentId?: string;   // for grouping cities under countries
    imageUrl?: string;   // for destination enrichment
    description?: string; // brief destination description
}

export interface ChatStepResult {
    step: ChatStep;
    chips: ChatChip[];
    message?: string; // AI enrichment message
    state: ChatSearchState;
}

export interface ChatSearchParams {
    searchType?: 'package' | 'hotel_only';
    destination?: string;
    departure?: string;
    checkIn?: string;
    nights?: number;
    adults?: number;
    children?: number;
    childrenAges?: number[];
}

export interface ChatSearchResult {
    success: boolean;
    results: (HolidayPackage | Hotel | Tour)[];
    searchParams: ChatSearchParams;
    error?: string;
}

// ==================== STATE ====================

const defaultState: ChatSearchState = {
    step: 'idle',
    searchType: 'package',
    departureId: '',
    departureName: '',
    departureType: 2,
    destinationId: '',
    destinationName: '',
    destinationType: 2,
    checkIn: '',
    nights: 0,
    adults: 2,
    children: 0,
    childrenAges: []
};

let chatState: ChatSearchState = { ...defaultState };

// Caches
let cachedDepartures: LocationOption[] = [];
let cachedArrivals: LocationOption[] = [];
let cachedDates: DateOption[] = [];
let cachedNights: NightOption[] = [];
let departuresCacheReady = false;

// Silent logger for background API calls
const silentLogger = (log: ApiLogEntry) => {
    console.log(`[ChatAPI] ${log.endpoint} - ${log.status}`);
};

// ==================== PUBLIC API ====================

/**
 * Get current chat search state
 */
export const getChatState = (): ChatSearchState => ({ ...chatState });

/**
 * Reset chat search to start over
 */
export const resetChatSearch = (): void => {
    chatState = { ...defaultState };
    cachedArrivals = [];
    cachedDates = [];
    cachedNights = [];
    console.log('[ChatAPI] Chat search state reset');
};

/**
 * Initialize departure cache — call on widget mount
 */
export const initializeLocationCache = async (): Promise<void> => {
    if (departuresCacheReady) return;

    try {
        const departures = await getDepartures(2, 'en-US', silentLogger);
        cachedDepartures = departures;
        departuresCacheReady = true;
        console.log(`[ChatAPI] Cached ${cachedDepartures.length} departure locations`);
        if (cachedDepartures.length > 0) {
            console.log('[ChatAPI] Departures:', cachedDepartures.map(d => `${d.name} (${d.id})`).join(', '));
        }
    } catch (e) {
        console.warn('[ChatAPI] Failed to cache departures:', e);
    }
};

/**
 * Step 0: Ask search type — hotel only or holiday package
 */
export const getChatSearchTypeChips = (): ChatStepResult => {
    chatState.step = 'ask_search_type';

    const chips: ChatChip[] = [
        { label: '✈️🏨 Flyg + Hotell', value: 'package', icon: '✈️' },
        { label: '🏨 Bara Hotell', value: 'hotel_only', icon: '🏨' },
    ];

    return {
        step: 'ask_search_type',
        chips,
        state: { ...chatState }
    };
};

/**
 * Set the search type and return state
 */
export const setChatSearchType = (searchType: 'package' | 'hotel_only'): void => {
    chatState.searchType = searchType;
    console.log(`[ChatAPI] Search type set to: ${searchType}`);
};

/**
 * Step 1: Get departure options as chat chips
 */
export const getChatDepartures = async (): Promise<ChatStepResult> => {
    if (!departuresCacheReady) {
        await initializeLocationCache();
    }

    chatState.step = 'ask_departure';

    const chips: ChatChip[] = cachedDepartures
        .filter(d => d.type === 2) // Only cities, not countries
        .map(d => ({
            label: d.name,
            value: d.id,
            icon: '✈️',
            type: d.type
        }));

    // If no type=2 cities, use all
    if (chips.length === 0) {
        cachedDepartures.forEach(d => {
            chips.push({
                label: d.name,
                value: d.id,
                icon: '✈️',
                type: d.type || 2
            });
        });
    }

    console.log(`[ChatAPI] Step 1: Offering ${chips.length} departure cities`);

    return {
        step: 'ask_departure',
        chips,
        state: { ...chatState }
    };
};

/**
 * Step 2: Set departure → get arrival (destination) options
 */
export const getChatArrivals = async (departureId: string, departureName: string, departureType: number = 2): Promise<ChatStepResult> => {
    chatState.departureId = departureId;
    chatState.departureName = departureName;
    chatState.departureType = departureType;
    chatState.step = 'ask_destination';

    const chips: ChatChip[] = [];

    try {
        const arrivals = await getArrivals(
            2,
            [{ Id: departureId, Type: departureType }],
            'en-US',
            silentLogger
        );
        cachedArrivals = arrivals;
        console.log(`[ChatAPI] Step 2: Got ${arrivals.length} arrivals from ${departureName} (${departureId})`);

        // Group by country (type=1) and cities (type=2)
        const countries = arrivals.filter(a => a.type === 1);
        const cities = arrivals.filter(a => a.type === 2);

        if (countries.length > 0) {
            for (const country of countries) {
                const countryCities = cities.filter(c => c.parentId === country.id);
                if (countryCities.length === 0) continue;

                // Add country header chip
                chips.push({
                    label: `🌍 ${country.name}`,
                    value: country.id,
                    icon: '🌍',
                    type: 1,
                    description: `${countryCities.length} destinations`
                });

                // Add city chips under this country
                for (const city of countryCities) {
                    chips.push({
                        label: city.name,
                        value: city.id,
                        icon: '🏖️',
                        type: city.type || 2,
                        parentId: country.id
                    });
                }
            }

            // Add orphan cities (no parent country)
            const orphans = cities.filter(c => !countries.find(co => co.id === c.parentId));
            for (const city of orphans) {
                chips.push({
                    label: city.name,
                    value: city.id,
                    icon: '🏖️',
                    type: city.type || 2
                });
            }
        } else {
            // No country grouping, just list cities
            arrivals.forEach(a => {
                chips.push({
                    label: a.name,
                    value: a.id,
                    icon: '🏖️',
                    type: a.type || 2
                });
            });
        }

        // Try to enrich with destination content from CMS
        await enrichChipsWithDestinationContent(chips);

    } catch (e) {
        console.warn('[ChatAPI] Failed to fetch arrivals:', e);
    }

    return {
        step: 'ask_destination',
        chips,
        state: { ...chatState }
    };
};

/**
 * Step 3: Set destination → get available check-in dates
 */
export const getChatDates = async (destinationId: string, destinationName: string, destinationType: number = 2): Promise<ChatStepResult> => {
    chatState.destinationId = destinationId;
    chatState.destinationName = destinationName;
    chatState.destinationType = destinationType;
    chatState.step = 'ask_dates';

    const chips: ChatChip[] = [];

    try {
        const depLocs = [{ Id: chatState.departureId, Type: chatState.departureType }];
        const arrLocs = [{ Id: destinationId, Type: destinationType }];

        const dates = await getCheckInDates(2, depLocs, arrLocs, 'en-US', silentLogger);
        cachedDates = dates;
        console.log(`[ChatAPI] Step 3: Got ${dates.length} available dates for ${destinationName}`);

        // Show at most 12 dates as chips
        const displayDates = dates.slice(0, 12);
        for (const d of displayDates) {
            const dateStr = d.date;
            // Format nicely: "15 Mar 2026"
            const dateObj = new Date(dateStr);
            const formatted = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

            chips.push({
                label: formatted,
                value: dateStr,
                icon: '📅'
            });
        }
    } catch (e) {
        console.warn('[ChatAPI] Failed to fetch dates:', e);
    }

    return {
        step: 'ask_dates',
        chips,
        state: { ...chatState }
    };
};

/**
 * Step 4: Set check-in date → get available night options
 */
export const getChatNights = async (checkIn: string): Promise<ChatStepResult> => {
    chatState.checkIn = checkIn;
    chatState.step = 'ask_nights';

    const chips: ChatChip[] = [];

    try {
        const depLocs = [{ Id: chatState.departureId, Type: chatState.departureType }];
        const arrLocs = [{ Id: chatState.destinationId, Type: chatState.destinationType }];

        const nights = await getNights(2, depLocs, arrLocs, checkIn, 'en-US', silentLogger);
        cachedNights = nights;
        console.log(`[ChatAPI] Step 4: Got ${nights.length} night options`);

        for (const n of nights) {
            chips.push({
                label: `${n.night} ${n.night === 1 ? 'night' : 'nights'}`,
                value: String(n.night),
                icon: '🌙'
            });
        }
    } catch (e) {
        console.warn('[ChatAPI] Failed to fetch nights:', e);
    }

    return {
        step: 'ask_nights',
        chips,
        state: { ...chatState }
    };
};

/**
 * Step 5: Set nights → ask for travelers
 */
export const setChatNights = (nights: number): ChatStepResult => {
    chatState.nights = nights;
    chatState.step = 'ask_travelers';

    // Predefined traveler options
    const chips: ChatChip[] = [
        { label: '1 Adult', value: '1-0', icon: '👤' },
        { label: '2 Adults', value: '2-0', icon: '👫' },
        { label: '2 Adults + 1 Child', value: '2-1', icon: '👨‍👩‍👦' },
        { label: '2 Adults + 2 Children', value: '2-2', icon: '👨‍👩‍👧‍👦' },
        { label: '3 Adults', value: '3-0', icon: '👥' },
        { label: '4 Adults', value: '4-0', icon: '👥' },
    ];

    return {
        step: 'ask_travelers',
        chips,
        state: { ...chatState }
    };
};

/**
 * Step 6: Set travelers → execute search
 */
export const executeChatSearch = async (adults: number, children: number, childrenAges: number[] = [], language: Language = 'en'): Promise<ChatSearchResult> => {
    chatState.adults = adults;
    chatState.children = children;
    chatState.childrenAges = childrenAges;
    chatState.step = 'searching';

    console.log('[ChatAPI] Step 6: Executing search with collected params:', {
        departure: `${chatState.departureName} (${chatState.departureId})`,
        destination: `${chatState.destinationName} (${chatState.destinationId}, type:${chatState.destinationType})`,
        checkIn: chatState.checkIn,
        nights: chatState.nights,
        adults,
        children
    });

    try {
        const query: SearchQuery = {
            type: chatState.searchType === 'hotel_only' ? SearchType.OnlyHotel : SearchType.PackageTours,
            departureId: chatState.departureId,
            departureName: chatState.departureName,
            departureType: chatState.departureType,
            destinationId: chatState.destinationId,
            destinationName: chatState.destinationName,
            destinationType: chatState.destinationType,
            checkIn: chatState.checkIn,
            nights: chatState.nights,
            rooms: 1,
            nationality: 'SE',
            currency: 'DKK',
            adults,
            children,
            childrenAges
        };

        const results = await searchTourvisio(query, language, silentLogger);
        console.log(`[ChatAPI] Search returned ${results.length} results`);

        chatState.step = 'done';

        return {
            success: true,
            results: results.slice(0, 6),
            searchParams: {
                destination: chatState.destinationName,
                departure: chatState.departureName,
                checkIn: chatState.checkIn,
                nights: chatState.nights,
                adults,
                children
            }
        };
    } catch (error) {
        console.error('[ChatAPI] Search failed:', error);
        chatState.step = 'done';
        return {
            success: false,
            results: [],
            searchParams: {
                destination: chatState.destinationName,
                departure: chatState.departureName
            },
            error: error instanceof Error ? error.message : 'Search failed'
        };
    }
};

/**
 * Quick search — fill all params at once and search (for smart detection)
 */
export const performChatSearch = async (
    searchParams: ChatSearchParams,
    language: Language = 'en'
): Promise<ChatSearchResult> => {
    // Ensure departures are cached
    if (!departuresCacheReady) {
        await initializeLocationCache();
    }

    // Resolve departure
    const depName = searchParams.departure || 'Stockholm';
    const depMatch = cachedDepartures.find(d =>
        d.name.toLowerCase().includes(depName.toLowerCase())
    );
    const departureId = depMatch?.id || '10';
    const departureType = depMatch?.type || 2;

    // Resolve destination
    const destName = searchParams.destination || 'Antalya';
    let destId = '';
    let destType = 2;

    // Try cache first
    if (cachedArrivals.length > 0) {
        const cached = cachedArrivals.find(a =>
            a.name.toLowerCase().includes(destName.toLowerCase()) ||
            destName.toLowerCase().includes(a.name.toLowerCase())
        );
        if (cached) {
            destId = cached.id;
            destType = cached.type || 2;
        }
    }

    // If not found, fetch arrivals for this departure
    if (!destId) {
        try {
            const arrivals = await getArrivals(2, [{ Id: departureId, Type: departureType }], 'en-US', silentLogger);
            cachedArrivals = arrivals;
            const found = arrivals.find(a =>
                a.name.toLowerCase().includes(destName.toLowerCase()) ||
                destName.toLowerCase().includes(a.name.toLowerCase())
            );
            if (found) {
                destId = found.id;
                destType = found.type || 2;
            }
        } catch (e) {
            console.warn('[ChatAPI] Failed to fetch arrivals for quick search:', e);
        }
    }

    // Still not found, try autocomplete
    if (!destId) {
        try {
            const results = await getArrivalAutoComplete(destName, 1, 'en-US', silentLogger);
            if (results.length > 0) {
                destId = results[0].id || results[0].Id;
                destType = results[0].type || results[0].Type || 2;
            }
        } catch (e) {
            console.warn('[ChatAPI] Autocomplete failed:', e);
        }
    }

    if (!destId) {
        return { success: false, results: [], searchParams, error: `Could not resolve destination: ${destName}` };
    }

    // Set state
    chatState = {
        step: 'searching',
        searchType: searchParams.searchType || 'package',
        departureId,
        departureName: depMatch?.name || depName,
        departureType,
        destinationId: destId,
        destinationName: destName,
        destinationType: destType,
        checkIn: searchParams.checkIn || getFutureDate(30),
        nights: searchParams.nights || 7,
        adults: searchParams.adults || 2,
        children: searchParams.children || 0,
        childrenAges: searchParams.childrenAges || []
    };

    return executeChatSearch(chatState.adults, chatState.children, chatState.childrenAges, language);
};

// ==================== HELPERS ====================

/**
 * Get a future date string (YYYY-MM-DD)
 */
const getFutureDate = (daysFromNow: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
};

/**
 * Enrich chips with destination content from CMS
 */
const enrichChipsWithDestinationContent = async (chips: ChatChip[]): Promise<void> => {
    try {
        const response = await fetch('/api/destinations');
        if (!response.ok) return;

        const data = await response.json();
        const destinations = data.destinations || [];

        for (const chip of chips) {
            if (chip.type !== 2) continue; // Only enrich cities

            const match = destinations.find((d: any) =>
                d.name?.toLowerCase().includes(chip.label.toLowerCase()) ||
                chip.label.toLowerCase().includes(d.name?.toLowerCase())
            );

            if (match) {
                chip.imageUrl = match.heroImage || match.image || '';
                chip.description = match.shortDescription || match.description?.substring(0, 100) || '';
            }
        }
    } catch (e) {
        console.warn('[ChatAPI] Destination enrichment failed:', e);
    }
};

/**
 * Format search results for AI context
 */
export const formatResultsForAI = (results: (HolidayPackage | Hotel | Tour)[]): string => {
    if (results.length === 0) return 'No results found.';

    return results.map((r, i) => {
        const pkg = r as HolidayPackage;
        return `${i + 1}. ${pkg.name} (${pkg.rating}⭐) - ${pkg.totalPrice} ${pkg.currency} - ${pkg.board} | Image: ${pkg.thumbnailUrl || 'N/A'} | ID: ${pkg.productId || pkg.id}`;
    }).join('\n');
};

/**
 * Generate booking URL from current state
 */
export const generateBookingUrl = (params?: ChatSearchParams): string => {
    const dest = params?.destination || chatState.destinationName || 'Antalya';
    const dep = params?.departure || chatState.departureName || 'Stockholm';
    const nights = params?.nights || chatState.nights || 7;
    const adults = params?.adults || chatState.adults || 2;
    const children = params?.children || chatState.children || 0;
    const checkIn = params?.checkIn || chatState.checkIn || '';

    const parts = [
        `destination=${encodeURIComponent(dest)}`,
        `departure=${encodeURIComponent(dep)}`,
        `nights=${nights}`,
        `adults=${adults}`,
    ];
    if (children > 0) parts.push(`children=${children}`);
    if (checkIn) parts.push(`checkIn=${checkIn}`);

    return `/holidays?${parts.join('&')}`;
};

/**
 * Get summary of collected params so far (for AI context)
 */
export const getCollectedParamsSummary = (): string => {
    const parts: string[] = [];
    if (chatState.departureName) parts.push(`Departure: ${chatState.departureName}`);
    if (chatState.destinationName) parts.push(`Destination: ${chatState.destinationName}`);
    if (chatState.checkIn) parts.push(`Check-in: ${chatState.checkIn}`);
    if (chatState.nights > 0) parts.push(`Nights: ${chatState.nights}`);
    if (chatState.adults > 0) parts.push(`Adults: ${chatState.adults}, Children: ${chatState.children}`);
    return parts.length > 0 ? `\nCollected so far: ${parts.join(' | ')}` : '';
};

/**
 * Get available departures (cached, for AI context)
 */
export const getCachedDepartureNames = (): string[] => {
    return cachedDepartures.filter(d => d.type === 2).map(d => d.name);
};

/**
 * Get available arrivals (cached, for AI context)
 */
export const getCachedArrivalNames = (): string[] => {
    return cachedArrivals.filter(a => a.type === 2).map(a => a.name);
};
